// src/whatsappChat/pages/TemplatesPage.jsx
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faRotate, faCopy, faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import { isMultiNumber, useWaTemplate, useWaTemplateNumbers, useWaTemplates } from '../api/templateHooks';
import { normalizeError } from '../api/client';
import { numberLabel, templateHeader } from '../api/content';
import { useWaChatContext } from '../components/WaChatLayout';
import { WA_CHAT_PATH } from '../components/WaChatNav';
import { Modal } from '../components/Modal';
import { buttonLabel, TemplatePreview, TemplateStatus } from '../components/TemplatePreview';
import { TemplateTestModal } from '../components/TemplateTestModal';
import '../styles/inbox.css';
import '../styles/compose.css';
import '../styles/templates.css';

const STATUSES = ['APPROVED', 'PENDING', 'REJECTED', 'PAUSED', 'DISABLED'];
const CATEGORIES = ['MARKETING', 'UTILITY', 'AUTHENTICATION'];
const CREATE_PATH = `${WA_CHAT_PATH}/templates/add`;

const titleCase = (value) => (value ? value.charAt(0) + value.slice(1).toLowerCase() : '');

// Only approved templates can be sent; the list returns the status in lower case.
const isApproved = (tpl) => String(tpl?.status || '').toUpperCase() === 'APPROVED';

function formatCreated(iso) {
  const date = iso ? new Date(iso) : null;
  if (!date || Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-GB', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

// The row's own number; the filter value can be 'all' or a list.
const rowNumberId = (tpl, numberId) => tpl?.number?.number_id || numberId;

function NumberCell({ number }) {
  if (!number) return <span className="wa-muted">—</span>;
  return (
    <>
      <div>{number.name || '—'}</div>
      <div className="wa-mono">{number.phone ?? number.number_id}</div>
    </>
  );
}

function TemplateDetail({ row, numberId, canCreate, canSend, onSendTest, onClose }) {
  const navigate = useNavigate();
  // The list can be a little behind; this asks for the current status.
  const { data, isFetching, refetch } = useWaTemplate(row.id, rowNumberId(row, numberId));
  const tpl = data || row;
  const buttons = Array.isArray(tpl.buttons) ? tpl.buttons : [];
  const header = templateHeader(tpl);

  return (
    <Modal
      title={tpl.name}
      badge={<TemplateStatus status={tpl.status} />}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="wa-btn wa-btn-secondary" onClick={() => refetch()} disabled={isFetching}>
            <FontAwesomeIcon icon={faRotate} /> {isFetching ? 'Checking…' : 'Check status'}
          </button>
          {canSend && isApproved(tpl) && (
            <button type="button" className="wa-btn wa-btn-secondary" onClick={() => onSendTest(tpl)}>
              <FontAwesomeIcon icon={faPaperPlane} /> Send test
            </button>
          )}
          {canCreate && (
            <button
              type="button"
              className="wa-btn wa-btn-primary"
              onClick={() => navigate(CREATE_PATH, { state: { from: tpl, numberId: rowNumberId(tpl, numberId) } })}
            >
              <FontAwesomeIcon icon={faCopy} /> Duplicate as new
            </button>
          )}
        </>
      }
    >
      {tpl.rejected_reason && (
        <div className="wa-alert wa-alert-danger">
          <strong>Rejected:</strong> {tpl.rejected_reason}
          <div>A rejected template cannot be edited. Duplicate it under a new name and resubmit.</div>
        </div>
      )}

      <TemplatePreview
        headerType={header.type}
        header={header.text}
        mediaUrl={header.mediaUrl}
        body={tpl.body}
        footer={tpl.footer}
        buttons={buttons.map(buttonLabel)}
      />

      <dl className="wa-meta wa-meta-after">
        <div>
          <dt>Category</dt>
          <dd>{titleCase(tpl.category) || '—'}</dd>
        </div>
        <div>
          <dt>Language</dt>
          <dd>{tpl.language || '—'}</dd>
        </div>
        <div>
          <dt>Number</dt>
          <dd>{tpl.number?.label || '—'}</dd>
        </div>
        <div>
          <dt>Type</dt>
          <dd>{titleCase(tpl.template_type) || 'Standard'}</dd>
        </div>
      </dl>
    </Modal>
  );
}

export default function TemplatesPage() {
  const { permissions } = useWaChatContext();
  const canCreate = Boolean(permissions.templates.create);
  const canSend = Boolean(permissions.inbox.send);

  const [numberId, setNumberId] = useState('');
  const [status, setStatus] = useState('');
  const [category, setCategory] = useState('');
  const [openRow, setOpenRow] = useState(null);
  const [testRow, setTestRow] = useState(null);

  const { data: numbers = [] } = useWaTemplateNumbers();
  const { templates, truncated, isLoading, isError, error, isFetching, refetch, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useWaTemplates({ numberId, status, category });

  return (
    <section className="wa-page wa-page-wide">
      <div className="wa-page-head">
        <div>
          <h1 className="wa-page-title">Templates</h1>
          <p className="wa-page-sub">
            Templates are approved by Meta before they can be sent. They cannot be edited or deleted.
          </p>
        </div>
        <div className="wa-page-actions">
          <button type="button" className="wa-btn wa-btn-secondary" onClick={() => refetch()} disabled={isFetching}>
            <FontAwesomeIcon icon={faRotate} /> {isFetching && !isFetchingNextPage ? 'Refreshing…' : 'Refresh'}
          </button>
          {canCreate && (
            <Link className="wa-btn wa-btn-primary" to={CREATE_PATH} state={{ numberId: isMultiNumber(numberId) ? '' : numberId }}>
              <FontAwesomeIcon icon={faPlus} /> Create template
            </Link>
          )}
        </div>
      </div>

      <div className="wa-toolbar">
        {numbers.length > 0 && (
          <select className="wa-select" value={numberId} onChange={(e) => setNumberId(e.target.value)} aria-label="Business number">
            <option value="">Default number</option>
            {numbers.length > 1 && <option value="all">All numbers</option>}
            {numbers.map((n) => (
              <option key={n.number_id} value={n.number_id}>{numberLabel(n)}</option>
            ))}
          </select>
        )}
        <select className="wa-select" value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
          <option value="">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>{titleCase(s)}</option>
          ))}
        </select>
        <select className="wa-select" value={category} onChange={(e) => setCategory(e.target.value)} aria-label="Category">
          <option value="">All categories</option>
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>{titleCase(c)}</option>
          ))}
        </select>
      </div>

      <div className="wa-card">
        {isLoading ? (
          <div className="wa-state">Loading templates…</div>
        ) : isError ? (
          <div className="wa-state">
            <p>{normalizeError(error).message}</p>
            <button type="button" className="wa-btn wa-btn-secondary" onClick={() => refetch()}>
              Try again
            </button>
          </div>
        ) : templates.length === 0 ? (
          <div className="wa-state">
            <p>{status || category ? 'No templates match these filters.' : 'No templates yet.'}</p>
            {canCreate && !status && !category && (
              <Link className="wa-btn wa-btn-primary" to={CREATE_PATH} state={{ numberId: isMultiNumber(numberId) ? '' : numberId }}>
                Create template
              </Link>
            )}
          </div>
        ) : (
          <table className="wa-table wa-table-click">
            <thead>
              <tr>
                <th>Name</th>
                <th>Number</th>
                <th>Category</th>
                <th>Language</th>
                <th>Status</th>
                <th>Created</th>
                <th>Body</th>
                {canSend && <th aria-label="Actions" />}
              </tr>
            </thead>
            <tbody>
              {templates.map((tpl) => (
                <tr
                  key={tpl.id}
                  tabIndex={0}
                  onClick={() => setOpenRow(tpl)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') setOpenRow(tpl);
                  }}
                >
                  <td className="wa-strong">{tpl.name}</td>
                  <td className="wa-nowrap"><NumberCell number={tpl.number} /></td>
                  <td>{titleCase(tpl.category) || '—'}</td>
                  <td>{tpl.language || '—'}</td>
                  <td>
                    <TemplateStatus status={tpl.status} />
                    {tpl.rejected_reason && <div className="wa-table-note">{tpl.rejected_reason}</div>}
                  </td>
                  <td className="wa-nowrap">{formatCreated(tpl.created_at)}</td>
                  <td className="wa-table-body">{tpl.body || <span className="wa-muted">{titleCase(tpl.template_type)}</span>}</td>
                  {canSend && (
                    <td className="wa-table-actions">
                      {isApproved(tpl) && (
                        <button
                          type="button"
                          className="wa-btn wa-btn-secondary wa-btn-sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setTestRow(tpl);
                          }}
                          onKeyDown={(e) => e.stopPropagation()}
                        >
                          <FontAwesomeIcon icon={faPaperPlane} /> Send test
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {truncated && (
        <div className="wa-list-more wa-muted">
          Showing the first templates only. Pick a single number to see the rest.
        </div>
      )}

      {hasNextPage && (
        <div className="wa-list-more">
          <button type="button" className="wa-btn wa-btn-secondary" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
            {isFetchingNextPage ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}

      {openRow && (
        <TemplateDetail
          row={openRow}
          numberId={numberId}
          canCreate={canCreate}
          canSend={canSend}
          onSendTest={(tpl) => {
            setOpenRow(null);
            setTestRow(tpl);
          }}
          onClose={() => setOpenRow(null)}
        />
      )}

      {testRow && <TemplateTestModal row={testRow} numberId={rowNumberId(testRow, numberId)} onClose={() => setTestRow(null)} />}
    </section>
  );
}
